import React, { useState } from 'react';
import { Button, Form, message, Space, Typography, Tabs, Card, Divider, Select, DatePicker, Radio, Row, Col } from 'antd';
import { ExportOutlined, InfoCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createDataExport } from './DataExportImportServices';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { useExportImportProgress } from '../../Contexts/ExportImportProgressContext';
import { Resource } from '../../Constants/socket';
import ResponsiveModal from '../Shared/ResponsiveModal/ResponsiveModal';
import SharedFilterBar from '../Shared/Filters/SharedFilterBar';
import { resourceFilterMapping, sharedExportFilters } from './exportFilterConfig';
import dayjs from 'dayjs';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const DataExportForm = ({ open, onClose, initialResource = null, initialFilters = null }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const { handleError } = useErrorHandler();
    const [loading, setLoading] = useState(false);
    const { addTask } = useExportImportProgress();

    // Filter state
    const [createdAtRange, setCreatedAtRange] = useState(null);
    const [resourceFilters, setResourceFilters] = useState({});

    // Initialize form with initial values when modal opens
    React.useEffect(() => {
        if (open && initialResource) {
            // Set initial resource
            form.setFieldValue('resources', [initialResource]);

            // Set initial filters if provided
            if (initialFilters && Object.keys(initialFilters).length > 0) {
                setResourceFilters({ [initialResource]: initialFilters });
            }
        }
    }, [open, initialResource, initialFilters, form]);

    // Reset form when modal closes
    React.useEffect(() => {
        if (!open) {
            form.resetFields();
            setCreatedAtRange(null);
            setResourceFilters({});
        }
    }, [open, form]);

    // Available resources for export
    const availableResources = [
        {
            value: Resource.AGENT,
            label: t('agents'),
            description: t('resource_descriptions.AGENT', 'AI agents and their configurations')
        },
        {
            value: Resource.KNOWLEDGE_BASE,
            label: t('knowledge_bases'),
            description: t('resource_descriptions.KNOWLEDGE_BASE', 'Knowledge bases and Q&A entries')
        },
        {
            value: Resource.CONTACT,
            label: t('contacts'),
            description: t('resource_descriptions.CONTACT', 'Customer contacts and profiles')
        },
        {
            value: Resource.CONVERSATION,
            label: t('conversations'),
            description: t('resource_descriptions.CONVERSATION', 'Conversation history')
        },
        {
            value: Resource.MESSAGE,
            label: t('messages'),
            description: t('resource_descriptions.MESSAGE', 'All messages and attachments')
        },
        {
            value: Resource.TRANSACTION,
            label: t('transactions'),
            description: t('resource_descriptions.TRANSACTION', 'Payment and coin transactions')
        }
    ];

    const allResourceValues = availableResources.map(r => r.value);

    // Helper to convert normalized filters back to filter array format for SharedFilterBar
    const convertNormalizedToFilterArray = (normalized, fields) => {
        if (!normalized || Object.keys(normalized).length === 0) return [];

        const filterArray = [];

        Object.entries(normalized).forEach(([key, value]) => {
            // Skip empty values
            if (value === undefined || value === null || value === '' ||
                (Array.isArray(value) && value.length === 0)) {
                return;
            }

            // Find the field config
            const field = fields.find(f => {
                // Check direct key match
                if (f.key === key) return true;

                // Check for range fields (dateRange, numberRange)
                if (f.type === 'dateRange' && (key === f.startKey || key === f.endKey)) {
                    return true;
                }
                if (f.type === 'numberRange' && (key === f.minKey || key === f.maxKey)) {
                    return true;
                }

                return false;
            });

            if (!field) return;

            // Check if this filter already exists in the array
            let existingFilter = filterArray.find(f => f.fieldKey === field.key);

            if (field.type === 'dateRange') {
                // For dateRange, we need both start and end keys
                if (!existingFilter) {
                    existingFilter = {
                        id: `${field.key}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                        fieldKey: field.key,
                        value: [null, null]
                    };
                    filterArray.push(existingFilter);
                }

                if (key === (field.startKey || `${field.key}_start`)) {
                    existingFilter.value[0] = value;
                } else if (key === (field.endKey || `${field.key}_end`)) {
                    existingFilter.value[1] = value;
                }
            } else if (field.type === 'numberRange') {
                // For numberRange, we need both min and max keys
                if (!existingFilter) {
                    existingFilter = {
                        id: `${field.key}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                        fieldKey: field.key,
                        value: [null, null]
                    };
                    filterArray.push(existingFilter);
                }

                if (key === (field.minKey || `${field.key}_min`)) {
                    existingFilter.value[0] = value;
                } else if (key === (field.maxKey || `${field.key}_max`)) {
                    existingFilter.value[1] = value;
                }
            } else {
                // For all other types
                filterArray.push({
                    id: `${field.key}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                    fieldKey: field.key,
                    value: value
                });
            }
        });

        return filterArray;
    };


    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const { resources } = values;

            if (!resources || resources.length === 0) {
                message.warning(t('at_least_one_resource_required', 'Please select at least one resource to export'));
                return;
            }

            // Build filters object - inject shared created_at filter into each resource
            const filters = {};

            // Prepare shared filter (created_at)
            const sharedFilter = {};
            if (createdAtRange && createdAtRange[0] && createdAtRange[1]) {
                const startBase = dayjs(createdAtRange[0]).startOf('day').format('YYYY-MM-DD[T]HH:mm:ss');
                const endBase = dayjs(createdAtRange[1]).endOf('day').format('YYYY-MM-DD[T]HH:mm:ss');
                sharedFilter.created_at_start = `${startBase}+00:00`;
                sharedFilter.created_at_end = `${endBase}+00:00`;
            }

            resources.forEach(resource => {
                const combinedFilters = {
                    ...sharedFilter,
                    ...(resourceFilters[resource] || {})
                };

                // Only add to filters if there are actual filter values
                if (Object.keys(combinedFilters).length > 0) {
                    filters[resource] = combinedFilters;
                }
            });

            const payload = {
                resources,
                format: values.format,
                ...(Object.keys(filters).length > 0 && { filters })
            };

            const response = await createDataExport(payload);

            // Add to progress tracker
            if (response?.data?.id) {
                const resourceNames = resources.map(r => t(r.toLowerCase())).join(', ');
                addTask({
                    id: response.data.id,
                    type: 'export',
                    name: `${resourceNames}.zip`,
                    status: 'processing',
                    progress: 0,
                    serverCreatedAt: response.server_datetime
                });
            }

            const text = resolveBackendMessage(t, response);
            if (text) message.success(text);
            else message.success(t('export_started_successfully', 'Export started successfully'));

            // Reset form and filters, then close modal
            form.resetFields();
            setCreatedAtRange(null);
            setResourceFilters({});
            onClose();

        } catch (error) {
            handleError(error, form);
        } finally {
            setLoading(false);
        }
    };

    const handleResourceChange = (selectedResources) => {
        // Clear filters for unchecked resources
        setResourceFilters(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(resourceKey => {
                if (!selectedResources.includes(resourceKey)) {
                    delete updated[resourceKey];
                }
            });
            return updated;
        });
    };

    const handleResourceFilterChange = (resourceKey, filtersArray, normalized) => {
        setResourceFilters(prev => ({
            ...prev,
            [resourceKey]: normalized
        }));
    };

    const selectedResources = Form.useWatch('resources', form) || [];

    // Get filter count for display (only resource-specific filters, not shared created_at)
    const getFilterCount = (resourceKey) => {
        const resourceSpecific = resourceFilters[resourceKey] || {};
        return Object.keys(resourceSpecific).filter(key => {
            const value = resourceSpecific[key];
            return value !== undefined && value !== null && value !== '' &&
                !(Array.isArray(value) && value.length === 0);
        }).length;
    };

    return (
        <ResponsiveModal
            title={t('create_export', 'Create Export')}
            open={open}
            onCancel={onClose}
            footer={
                <Space>
                    <Button onClick={onClose}>
                        {t('cancel')}
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        icon={<ExportOutlined />}
                        onClick={() => form.submit()}
                    >
                        {t('create_export', 'Create Export')}
                    </Button>
                </Space>
            }
            desktopWidth={800}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{ resources: [], format: 'json' }}
                requiredMark={false}
                colon={false}
            >
                {/* Two-column layout for desktop */}
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        {/* Resource Selection */}
                        <Form.Item
                            name="resources"
                            label={<Text strong>{t('select_resources_to_export', 'Select Resources to Export')}</Text>}
                            rules={[
                                {
                                    required: true,
                                    message: t('at_least_one_resource_required', 'Please select at least one resource to export')
                                }
                            ]}
                            style={{ marginBottom: 12 }}
                        >
                            <Select
                                mode="multiple"
                                placeholder={t('select_resources_placeholder', 'Select resources to export')}
                                onChange={handleResourceChange}
                                options={availableResources.map(resource => ({
                                    value: resource.value,
                                    label: resource.label,
                                    title: resource.description
                                }))}
                                maxTagCount="responsive"
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        {/* Export Format */}
                        <Form.Item
                            name="format"
                            label={<Text strong>{t('export_format', 'Export Format')}</Text>}
                            style={{ marginBottom: 12 }}
                        >
                            <Select
                                options={[
                                    { value: 'json', label: 'JSON' },
                                    { value: 'csv', label: 'CSV' }
                                ]}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Created At Filter - Always visible */}
                <Form.Item
                    label={<Text strong>{t('filters_field_created_at', 'Created Date Range')}</Text>}
                    style={{ marginBottom: 12 }}
                >
                    <RangePicker
                        value={createdAtRange}
                        onChange={setCreatedAtRange}
                        style={{ width: '100%' }}
                        placeholder={[t('start_date', 'Start date'), t('end_date', 'End date')]}
                    />
                </Form.Item>

                {/* Filters Section */}
                {selectedResources.length > 0 && selectedResources.some(r => resourceFilterMapping[r]?.length > 0) && (
                    <>
                        <Divider style={{ margin: '8px 0' }} />
                        <Card
                            size="small"
                            title={
                                <Space>
                                    <FilterOutlined />
                                    <Text strong>{t('resource_specific_filters', 'Resource-specific filters')}</Text>
                                </Space>
                            }
                            style={{ marginBottom: 12 }}
                        >
                            <Tabs
                                items={selectedResources
                                    .filter(resourceKey => resourceFilterMapping[resourceKey]?.length > 0)
                                    .map(resourceKey => {
                                        const resource = availableResources.find(r => r.value === resourceKey);
                                        const filterCount = getFilterCount(resourceKey);

                                        return {
                                            key: resourceKey,
                                            label: (
                                                <Space size={4}>
                                                    {resource.label}
                                                    {filterCount > 0 && (
                                                        <Text type="secondary" style={{ fontSize: '11px' }}>
                                                            ({filterCount})
                                                        </Text>
                                                    )}
                                                </Space>
                                            ),
                                            children: (
                                                <SharedFilterBar
                                                    fields={resourceFilterMapping[resourceKey] || []}
                                                    value={convertNormalizedToFilterArray(
                                                        resourceFilters[resourceKey] || {},
                                                        resourceFilterMapping[resourceKey] || []
                                                    )}
                                                    onChange={(filtersArray, normalized) => handleResourceFilterChange(resourceKey, filtersArray, normalized)}
                                                />
                                            )
                                        };
                                    })
                                }
                            />
                        </Card>
                    </>
                )}


            </Form>
        </ResponsiveModal>
    );
};

export default DataExportForm;
